/* @flow */
import 'rc-slider/assets/index.css';

import * as React from 'react';
import styled from 'styled-components';

import { Box, Button as Btn, Flex, Space, Text, Table } from '@sonyged/proc-ui';
import { Button, ButtonIcon, Footer } from '@sonyged/ext-proc';
import css from '@styled-system/css';

import { HistoricalInfoModal } from './HistoricalInfoModal';
import { PriceSlider } from '../../ice/fields/PriceSlider';
import { StockSlider } from './StockSlider';
import AppContext from '../../../AppContext';
import Constants from '../../ice/fields/Constants';
import IceContext from '../../ice/fields/IceContext';
import ProcSelect from '../../ice/fields/ProcSelect';

type Props = {
  iceCtx: IceContext,
  wagon: number,
  title: string,
  onPreviousClick: Function,
  onNextClick: Function,
  onPlaceChange: Function,
  onIceChange: Function,
  onPriceChange: Function,
  onStockNumChange: Function,
};

type State = {
  priceChanged: boolean,
  stockChanged: boolean,
  modalOpen: boolean,
};

class StockConstants extends Constants {
  static STOCK_BANANA = 0;
  static STOCK_CHOCO = 1;
  static STOCK_VANILLA = 2;
  static STOCK_STRAWBERRY = 3;
  static STOCKS = [this.STOCK_BANANA, this.STOCK_CHOCO, this.STOCK_VANILLA, this.STOCK_STRAWBERRY];
  static STOCKS_NAME = {
    [0]: 'banana',
    [1]: 'choco',
    [2]: 'vanilla',
    [3]: 'strawberry',
  };

  static getStockImages() {
    return this.STOCKS.map(v => {
      return `../../images/pages/ice-a/fields/fi_ice_ice-box_${this.STOCKS_NAME[v]}@2x.png`;
    });
  }

  static getStockImage(stock) {
    return `../../images/pages/ice-a/fields/fi_ice_ice-box_${this.STOCKS_NAME[stock]}@2x.png`;
  }
}

export default class SettingContent extends React.Component<Props, State> {
  static contextType = AppContext;
  inputs: any[];

  constructor(props: Props) {
    super(props);
    this.inputs = [];
    this.state = {
      priceChanged: false,
      stockChanged: false,
      modalOpen: false,
    };
  }

  componentDidMount() {
    const input = this.inputs.find(Boolean);
    if (input) {
      input.focus();
    }
  }

  render() {
    return (
      <div className={'proc-ice-field-content setting ice-advance'}>
        <div className={`proc-contents padding ${'tertiary100-bg-color'}`}>
          <div className={`proc-outer`}>
            <div className={'proc-inner small status'} style={{ maxWidth: '800px !important' }}>
              <div className="header">
                <div className="left" style={{ position: 'static' }}>
                  <div className={'weather secondary100-fill-color'}>
                    {Constants.getWetherSvg(this.props.iceCtx.weather)}
                  </div>
                  <div className={'temperature secondary100-color'}>
                    <span className={'proc-huge-font'}>{this.props.iceCtx.temperature}</span>
                    <span className={'proc-medium-font'}>度</span>
                  </div>
                </div>
                <div className="center">
                  <img className="image" src={Constants.getStatusWagonIcon(this.props.wagon)} />
                </div>
                <div className="right">
                  <PillButton onClick={() => this.setState({ modalOpen: true })}>調べる</PillButton>
                  <HistoricalInfoModal
                    day={this.props.iceCtx.weekday}
                    isOpen={this.state.modalOpen}
                    onClose={() => this.setState({ modalOpen: false })}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={`setting-container gray10-bg-color ${'border-top secondary20-border-color'}`}>
            <div className={'proc-outer'}>
              <div className={'proc-inner small'}>
                <Flex flexDirection="column" alignItems="center">
                  <Space mt={[8, 8, 12]}>
                    <Text color="gray100" fontSize={[18, 18, 22]} lineHeight={1}>
                      せっていしよう
                    </Text>
                  </Space>
                  <Space my={[12, 12, 24]}>
                    <Text color="gray100" fontSize={[14, 14, 17]} lineHeight={1}>
                      「調べる」を見て売るアイスをえらぼう
                    </Text>
                  </Space>
                </Flex>
                {this._renderStockInfo()}
                <Box height={[16, 16, 24]} />
                {this._renderSellInfo()}
                <Box height={[16, 16, 24]} />
              </div>
            </div>
          </div>
        </div>

        <Footer.action className="white-bg-color">
          <Button.normal onClick={this.props.onPreviousClick}>
            <ButtonIcon.back />
          </Button.normal>

          <Button.attention
            disabled={this.props.iceCtx.place < 0 || this.props.iceCtx.ice < 0}
            onClick={this.props.onNextClick}
          >
            <ButtonIcon.action />
          </Button.attention>
        </Footer.action>
      </div>
    );
  }

  _renderNextButton() {
    if (this.props.iceCtx.place < 0 || this.props.iceCtx.ice < 0) {
      return <Button.attention disabled>つぎへ</Button.attention>;
    }
    return <Button.attention onClick={this.props.onNextClick}>つぎへ</Button.attention>;
  }

  _renderPlaceSelect() {
    return (
      <ProcSelect
        innerRef={r => {
          this.inputs[2] = r;
        }}
        title={'場所'}
        value={this.props.iceCtx.place}
        images={Constants.getPlaceIcons()}
        labels={Constants.PLACE_LABELS_ADV}
        tabIndex={0}
        onSelected={this.props.onPlaceChange}
      />
    );
  }

  _renderIceSelect() {
    return (
      <ProcSelect
        innerRef={r => {
          this.inputs[0] = r;
        }}
        title={'アイス'}
        value={this.props.iceCtx.ice}
        images={Constants.getIceImages()}
        labels={Constants.ICE_LABELS}
        tabIndex={0}
        onSelected={this.props.onIceChange}
      />
    );
  }

  _renderStockSelect() {
    return (
      <ProcSelect
        innerRef={r => {
          this.inputs[1] = r;
        }}
        title={'アイス'}
        value={this.props.iceCtx.ice}
        images={StockConstants.getStockImages()}
        labels={Constants.ICE_LABELS}
        tabIndex={0}
        onSelected={this.props.onIceChange}
      />
    );
  }

  _renderPriceSlider() {
    return (
      <PriceSlider
        value={this.props.iceCtx.price}
        min={Constants.MIN_PRICE}
        max={Constants.MAX_PRICE}
        step={Constants.PRICE_STEP}
        onChange={this._onPriceChange.bind(this)}
      />
    );
  }

  _renderStockSlider() {
    return (
      <StockSlider
        value={this.props.iceCtx.stockNum}
        // min={Constants.MIN_PRICE}
        min={10}
        max={Constants.MAX_PRICE}
        step={Constants.PRICE_STEP}
        onChange={this._onStockNumChange.bind(this)}
      />
    );
  }

  _renderPreviousButton() {
    if (!this.props.onPreviousClick) {
      return <span />;
    }
    return (
      <Button.normal onClick={this.props.onPreviousClick}>
        <ButtonIcon.back />
      </Button.normal>
    );
  }

  _renderStockInfo() {
    const { stockNum } = this.props.iceCtx;
    return (
      <IceSettingTable className={'ice-advance'}>
        <thead>
          <tr>
            <th colSpan="2">仕入れる</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>アイス</td>
            <td>{this._renderStockSelect()}</td>
          </tr>
          <tr>
            <td>仕入れる数</td>
            <td>{this._renderStockSlider()}</td>
          </tr>
          <tr>
            <td>仕入れのねだん</td>
            <td className="text-center">
              <StockingText>
                {stockNum * Constants.DEFAULT_PRICE}
                <span> 円</span>
              </StockingText>
            </td>
          </tr>
        </tbody>
      </IceSettingTable>
    );
  }

  _renderSellInfo = () => {
    return (
      <IceSettingTable className={'ice-advance'}>
        <thead>
          <tr>
            <th colSpan="2">売る</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>場所</td>
            <td>{this._renderPlaceSelect()}</td>
          </tr>
          <tr>
            <td>売るねだん</td>
            <td>{this._renderPriceSlider()}</td>
          </tr>
        </tbody>
      </IceSettingTable>
    );
  };

  _onPriceChange(price: number) {
    this.context.soundController.sePlay('nedan_bar');
    this.setState({ priceChanged: true });
    if (this.props.onPriceChange) {
      this.props.onPriceChange(price);
    }
  }

  _onStockNumChange(stockNum: number) {
    this.context.soundController.sePlay('nedan_bar');
    this.setState({ stockChanged: true });
    if (this.props.onStockNumChange) {
      this.props.onStockNumChange(stockNum);
    }
  }
}

const IceSettingTable = styled(Table).attrs({ borderRadius: [8, 8, 12] })(
  css({
    width: '100%',

    'th, td': {
      px: [12, 12, 16],
      fontSize: ['mobile.small', 'smallPC.small', 20],
      lineHeight: 'title',
    },

    'thead th': {
      py: [6],
      color: 'secondary100',
      textAlign: 'left',
      fontWeight: 'normal',
    },

    'tbody td': {
      color: 'gray100',
      bg: 'white',
      py: [10, 10, 12],
    },

    'td:first-child': {
      width: '30%',
    },

    '.rc-slider-disabled': {
      background: 'transparent',
    },
    '.rc-slider-track': {
      bg: '#ffd5e3',
      height: [4, 4, 6],
    },
    '.rc-slider-rail': {
      height: [4, 4, 6],
    },
    '.rc-slider-handle': {
      border: 'solid 2px white',
      bg: '#ffd5e3',
      ml: [-13, -13, -19],
      mt: [-11, -11, -17],
      size: [26, 26, 38],
    },
    '.rc-slider-handle:active, .rc-slider-handle.dragging': {
      boxShadow: '0 0 5px #ffd5e3',
      ml: [-15, -15, -20],
      mt: [-13, -13, -18],
      size: [30, 30, 44],
    },
    '.rc-slider-handle:focus': {
      boxShadow: '0 0 5px #ffd5e3',
    },
    '.price-slider': {
      px: [13 + 12, 13 + 12, 19 + 16],
      pt: [11 + 20 + 4, 11 + 20 + 14, 17 + 22 + 4],
      pb: [11 + 4, 11 + 4, 17 + 4],
      overflow: 'hidden',
      '.rc-slider-mark': {
        top: [-32, -32, -42],
        fontSize: [18, 18, 26],
      },
      '.rc-slider-mark-text-active': {
        color: 'inherit',
        '&::after': {
          content: "'円'",
          fontSize: [15, 15, 22],
        },
      },
      '.rc-slider-handle.dragging + .rc-slider-mark': {
        display: 'none',
      },
    },
    '.price-slider.stock .rc-slider-mark-text-active::after': {
      content: "'こ'",
    },
  })
);

const StockingText = styled('span')(
  css({
    fontSize: ['mobile.medium', 'smallPC.medium', '24px'],
    span: {
      fontSize: ['mobile.small', 'smallPC.small', '20px'],
    },
  })
);

const PillButton = styled(Btn).attrs({ variant: 'outline' })(
  css({
    height: [36, 36, 48],
    width: [104, 104, 216],
    pr: 0,
    pl: 0,
    bg: '#fff',
    fontSize: [14, 14, 20],
  })
);
